import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Star, Flame, Sparkles, Lock, CheckCircle2, ClipboardCheck, ArrowRight } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { dataLayer } from '../../data/dataLayer';
import { Card } from '../ui/Card';
import { AchievementsSection } from './AchievementsSection';
import { MilestonesSection } from './MilestonesSection';
import { ErrorPatternsSection } from './ErrorPatternsSection';
import { DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { WorldZone } from '../shared/WorldZone';
import { Button } from '../ui/Button';
import type { AssessmentReport } from '../../assessment';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const activeAccountId = accounts?.activeAccountId;
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  useEffect(() => {
    if (activeAccountId) {
      dataLayer.getDjayLongPressDiscovered(activeAccountId).then(setHasDiscoveredLongPress);
    }
  }, [activeAccountId]);
  // Calculate overall stats
  const totalQuestions = MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0);
  // Calculate overall progress
  const {
    masteredCount,
    inProgressCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    let mastered = 0;
    let inProgress = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 85) mastered++;else if (answered > 0 || mastery > 0) inProgress++;
    });
    return {
      masteredCount: mastered,
      inProgressCount: inProgress,
      totalQuestionsAnswered: totalQs
    };
  }, [getModuleMasteryForGroup, getAnsweredQuestions]);
  // Calculate streak (simplified for this view)
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  type MascotRarity = 'common' | 'uncommon' | 'rare' | 'legendary' | 'secret';
  const RARITY_STYLES: Record<MascotRarity, {
    border: string;
    bg: string;
    text: string;
    glow: string;
    label: string;
  }> = {
    common: {
      border: 'border-slate-200',
      bg: 'bg-slate-50',
      text: 'text-slate-600',
      glow: '',
      label: 'Gewoon'
    },
    uncommon: {
      border: 'border-emerald-300',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]',
      label: 'Ongewoon'
    },
    rare: {
      border: 'border-amber-300',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
      label: 'Zeldzaam'
    },
    legendary: {
      border: 'border-purple-400',
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      glow: 'shadow-[0_0_20px_rgba(167,139,250,0.5)]',
      label: 'Legendarisch'
    },
    secret: {
      border: 'border-rose-400',
      bg: 'bg-rose-50',
      text: 'text-rose-700',
      glow: 'shadow-[0_0_20px_rgba(244,63,94,0.5)]',
      label: 'Geheim'
    }
  };
  // Determine unlocked special Djays based on achievements logic
  const mascotCards = useMemo(() => {
    return [{
      variant: 'default' as DjayVariant,
      name: 'Standaard Djay',
      description: 'Altijd beschikbaar',
      unlocked: true,
      rarity: 'common' as MascotRarity,
      progress: {
        current: 1,
        max: 1,
        label: 'Ontgrendeld'
      }
    }, {
      variant: 'pixel' as DjayVariant,
      name: 'DJ Pixel',
      description: 'Honderdtal bereikt',
      unlocked: totalQuestionsAnswered >= 100,
      rarity: 'uncommon' as MascotRarity,
      progress: {
        current: Math.min(totalQuestionsAnswered, 100),
        max: 100,
        label: 'vragen'
      }
    }, {
      variant: 'blaze' as DjayVariant,
      name: 'DJ Blaze',
      description: '3 volle weken achter elkaar',
      unlocked: consecutiveFullWeeks >= 3,
      rarity: 'rare' as MascotRarity,
      progress: {
        current: Math.min(consecutiveFullWeeks, 3),
        max: 3,
        label: 'weken'
      }
    }, {
      variant: 'frost' as DjayVariant,
      name: 'DJ Frost',
      description: '3 vakken afgerond',
      unlocked: masteredCount >= 3,
      rarity: 'rare' as MascotRarity,
      progress: {
        current: Math.min(masteredCount, 3),
        max: 3,
        label: 'vakken'
      }
    }, {
      variant: 'royal' as DjayVariant,
      name: 'DJ Royal',
      description: 'Alle vakken afgerond',
      unlocked: masteredCount >= MODULES.length,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(masteredCount, MODULES.length),
        max: MODULES.length,
        label: 'vakken'
      }
    }, {
      variant: 'cosmic' as DjayVariant,
      name: 'DJ Cosmic',
      description: 'Level 15 bereikt',
      unlocked: state.stats.level >= 15,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(state.stats.level, 15),
        max: 15,
        label: 'levels'
      }
    }, {
      variant: 'shadow' as DjayVariant,
      name: 'DJ Shadow',
      description: '30.000 XP behaald',
      unlocked: state.stats.xp >= 30000,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(state.stats.xp, 30000),
        max: 30000,
        label: 'XP'
      }
    }, {
      variant: 'goud' as DjayVariant,
      name: hasDiscoveredLongPress ? 'DJ Goud' : '???',
      description: hasDiscoveredLongPress ? 'Geheim ontdekt!' : 'Geheim! Ontdek het zelf...',
      unlocked: hasDiscoveredLongPress,
      rarity: 'secret' as MascotRarity,
      progress: {
        current: hasDiscoveredLongPress ? 1 : 0,
        max: 1,
        label: 'Geheim'
      }
    }];
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state.stats.level, state.stats.xp, hasDiscoveredLongPress]);
  return <PageContainer className="pb-20" data-id="element-1707">
      {/* Hero Header with Djay */}
      <motion.div variants={itemVariants} data-id="element-1708">
        <PageHero title="Mijn Voortgang" subtitle="Bekijk je leertips, mijlpalen en achievements! 📊" gradientClasses="from-indigo-500 via-violet-500 to-purple-500" djayEmotion="celebrating" djayVariant={selectedVariant} icon={TrendingUp} iconBgClass="bg-indigo-400" floatingEmojis={['📊', '⭐']} data-id="element-1709">
          <HeroStatPill icon={Star} data-id="element-1710">Level {state.stats.level}</HeroStatPill>
          <HeroStatPill icon={Sparkles} data-id="element-1711">
            {state.stats.xp.toLocaleString('nl-NL')} XP
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1712">
            {currentStreak} dagen deze week
          </HeroStatPill>
        </PageHero>
      </motion.div>

      {/* Error Patterns Section (Slimme Leertips) — FIRST for actionability */}
      <motion.div variants={itemVariants} data-id="element-1713">
        <ErrorPatternsSection state={state} selectedGroup={selectedGroup} data-id="element-1714" />
      </motion.div>

      {/* Milestones Section */}
      <motion.div variants={itemVariants} data-id="element-1715">
        <MilestonesSection state={state} selectedGroup={selectedGroup} data-id="element-1716" />
      </motion.div>

      {/* Achievements Section */}
      <motion.div variants={itemVariants} data-id="element-1717">
        <AchievementsSection state={state} totalQuestionsAnswered={totalQuestionsAnswered} masteredCount={masteredCount} currentStreak={currentStreak} data-id="element-1718" />
      </motion.div>

      {/* Mascotte Collectie */}
      <motion.div variants={itemVariants} className="space-y-4" data-id="element-1719">
        <div className="flex items-center justify-between px-1" data-id="element-1720">
          <h3 className="text-xl font-black text-slate-800 flex items-center gap-2" data-id="element-1721">
            <Sparkles className="w-6 h-6 text-orange-500" data-id="element-1722" /> Mascotte Collectie
          </h3>
          <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-bold shadow-sm" data-id="element-1723">
            {mascotCards.filter(c => c.unlocked).length}/{mascotCards.length}{' '}
            Verzameld
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1724">
          {mascotCards.map(card => {
          const isSelected = selectedVariant === card.variant;
          const styles = RARITY_STYLES[card.rarity];
          return <button key={card.variant} onClick={() => card.unlocked && !isSelected && setSelectedVariant(card.variant)} className={`relative flex flex-col text-left rounded-2xl border-2 transition-all duration-300 overflow-hidden
                  ${card.unlocked ? `${styles.border} ${styles.bg} ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 scale-[1.02]' : 'hover:scale-[1.02] hover:shadow-md'} ${styles.glow}` : 'border-slate-200 bg-slate-50 opacity-80 cursor-not-allowed'}
                `} data-id="element-1725">
                {/* Rarity Badge */}
                <div className="absolute top-2 left-2 z-10" data-id="element-1726">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/80 backdrop-blur-sm ${styles.text} shadow-sm`} data-id="element-1727">
                    {styles.label}
                  </span>
                </div>

                {/* Active Badge */}
                {isSelected && <div className="absolute top-2 right-2 z-10" data-id="element-1728">
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white shadow-sm" data-id="element-1729">
                      Actief
                    </span>
                  </div>}

                {/* Locked Icon */}
                {!card.unlocked && <div className="absolute top-2 right-2 z-10" data-id="element-1730">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center shadow-inner" data-id="element-1731">
                      <Lock className="w-3 h-3 text-slate-400" data-id="element-1732" />
                    </div>
                  </div>}

                {/* Avatar Area */}
                <div className={`h-32 flex items-center justify-center relative ${card.unlocked ? '' : 'grayscale'}`} data-id="element-1733">
                  {/* Background pattern/glow for unlocked */}
                  {card.unlocked && <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white to-transparent" data-id="element-1734" />}

                  <div className="relative z-10 transform scale-125 mt-4" data-id="element-1735">
                    {card.rarity === 'secret' && !card.unlocked ? <div className="text-4xl font-black text-slate-300" data-id="element-1736">
                        ???
                      </div> : <DjayAvatar size="lg" emotion={card.unlocked ? isSelected ? 'excited' : 'proud' : 'sleeping'} variant={card.variant} animate={card.unlocked} data-id="element-1737" />}
                  </div>
                </div>

                {/* Content Area */}
                <div className="p-3 bg-white flex-1 flex flex-col border-t border-slate-100" data-id="element-1738">
                  <h4 className={`font-black text-sm mb-1 ${card.unlocked ? 'text-slate-800' : 'text-slate-500'}`} data-id="element-1739">
                    {card.name}
                  </h4>
                  <p className="text-[10px] text-slate-500 font-medium leading-snug mb-3 flex-1" data-id="element-1740">
                    {card.description}
                  </p>

                  {/* Progress Bar */}
                  <div className="mt-auto" data-id="element-1741">
                    <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-1" data-id="element-1742">
                      <span data-id="element-1743">Voortgang</span>
                      <span data-id="element-1744">
                        {card.progress.current} / {card.progress.max}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1745">
                      <div className={`h-full rounded-full transition-all duration-500 ${card.unlocked ? 'bg-emerald-500' : 'bg-slate-300'}`} style={{
                    width: `${card.progress.current / card.progress.max * 100}%`
                  }} data-id="element-1746" />
                    </div>
                  </div>
                </div>
              </button>;
        })}
        </div>
      </motion.div>
    </PageContainer>;
}